import { ReactNode, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { trpc, createTrpcLinksWithRefresh } from "../lib/api/trpc";
import { notify } from "../utils/notify";

interface TrpcProviderProps {
  children: ReactNode;
}

export const TrpcProvider = ({ children }: TrpcProviderProps) => {
  // Create a query client with optimized settings
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Avoid refetching on window focus for a better user experience
            refetchOnWindowFocus: false,

            // Retry failed queries only for network errors, not for 4xx or 5xx responses
            retry: (failureCount, error: any) => {
              const status = error?.data?.httpStatus;
              // Don't retry client errors (4xx)
              if (status && status >= 400 && status < 500) return false;
              // Retry server errors (5xx) up to 3 times
              if (status && status >= 500) return failureCount < 3;
              // For network errors, retry up to 3 times
              return failureCount < 3;
            },

            // Keep data fresh for 5 minutes
            staleTime: 5 * 60 * 1000,

            // Keep cached data for 30 minutes when not in use
            cacheTime: 30 * 60 * 1000,

            // Global error handler
            onError: (error: any) => {
              if (error?.data?.code === "FORBIDDEN") {
                notify.error(
                  "You do not have permission to perform this action",
                );
              }
            },
          },
          mutations: {
            // Don't retry mutations by default
            retry: false,

            // Global error handler for mutations
            onError: (error: any) => {
              if (error?.data?.code === "FORBIDDEN") {
                notify.error(
                  "You do not have permission to perform this action",
                );
              }
            },
          },
        },
      }),
  );

  // Create the tRPC client with automatic token refresh
  // @ts-ignore - Using createClient with AnyRouter type
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: createTrpcLinksWithRefresh(),
    }),
  );

  // @ts-ignore - Using Provider with AnyRouter type
  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
};
